import { useEffect } from "react";
import { Navbar } from "@/components/okaia/Navbar";
import { Footer } from "@/components/okaia/Footer";
import { Reveal, CTAButton } from "@/components/okaia/Reveal";
import { TIDYCAL_URL } from "@/config/links";
import { Check } from "lucide-react";

const GETS = [
  "Un état des lieux synthétique de votre projet ou de votre activité.",
  "Une priorité clairement identifiée.",
  "Cinq actions adaptées à vos moyens : trois prioritaires et deux à engager ensuite.",
  "Une synthèse écrite pour avancer après l'entretien.",
];

const STEPS = [
  "Préparer — Lors de la réservation, vous répondez à quatre questions courtes pour présenter votre situation. Trois sont obligatoires et la dernière est facultative.",
  "Échanger — Nous échangeons pendant 30 minutes en visioconférence sur votre objectif, votre principal blocage et les démarches déjà tentées.",
  "Avancer — Après l'entretien, vous recevez une synthèse écrite avec votre priorité et cinq actions : trois prioritaires et deux à engager ensuite.",
];

export default function ClarifierPage() {
  useEffect(() => {
    document.title = "Diagnostic Business OKAÏA Dév — 30 minutes, 49 €";
    const meta = document.querySelector('meta[name="description"]');
    const original = meta?.getAttribute("content");
    meta?.setAttribute(
      "content",
      "Le Diagnostic Business OKAÏA Dév : 30 minutes en visioconférence pour identifier votre principal blocage, une priorité et cinq actions, avec synthèse écrite. 49 €."
    );
    const canonical = document.querySelector('link[rel="canonical"]');
    const originalCanonical = canonical?.getAttribute("href");
    canonical?.setAttribute("href", `${window.location.origin}/clarifier-mon-besoin`);
    return () => {
      document.title = "OKAÏA Dév — Diagnostic Business 30 min et orientation formation gratuite";
      if (original) meta?.setAttribute("content", original);
      if (originalCanonical) canonical?.setAttribute("href", originalCanonical);
    };
  }, []);

  return (
    <main data-testid="clarifier-page">
      <Navbar />
      <section className="bg-[#0B2215] px-6 pb-16 pt-40 md:px-12">
        <div className="mx-auto max-w-[1400px]">
          <p className="okaia-overline mb-6 flex items-center gap-4 text-[#C8A97E]">
            <span className="inline-block h-px w-12 bg-[#C8A97E]" />
            L'offre principale
          </p>
          <h1 className="max-w-4xl font-serif-display text-4xl leading-[1.1] tracking-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
            Diagnostic Business OKAÏA&nbsp;Dév
          </h1>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#A3B1AA] md:text-lg">
            Un entretien individuel en visioconférence de 30 minutes pour identifier votre
            principal blocage, choisir une priorité et repartir avec une synthèse écrite et
            cinq actions&nbsp;: trois prioritaires et deux à engager ensuite.
          </p>
          <div className="mt-10">
            <CTAButton label="Réserver mon diagnostic — 49 €" testId="clarifier-cta" href={TIDYCAL_URL} />
            <p className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-[#A3B1AA]">
              30 minutes • Visioconférence • Synthèse écrite et cinq actions
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F9F8F6] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <h2 className="font-serif-display text-3xl tracking-tight text-[#0B2215] md:text-4xl">
                  Comment se déroule le diagnostic&nbsp;?
                </h2>
                <ol className="mt-8">
                  {STEPS.map((s, i) => (
                    <li key={i} data-testid={`diag-step-${i + 1}`} className="flex items-start gap-5 border-t border-[#0B2215]/10 py-6 last:border-b">
                      <span className="font-serif-display text-3xl italic text-[#C8A97E]">0{i + 1}</span>
                      <p className="font-serif-display text-lg leading-snug text-[#0B2215] md:text-xl">{s}</p>
                    </li>
                  ))}
                </ol>
                <p className="mt-6 text-sm font-light italic text-[#4A5A52]">
                  Les quatre questions préparatoires se remplissent lors de la réservation sur
                  TidyCal — elles permettent de tirer le meilleur des 30 minutes d'échange.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <h2 className="mt-14 font-serif-display text-3xl tracking-tight text-[#0B2215] md:text-4xl">
                  Ce que vous obtenez
                </h2>
                <ul className="mt-8 space-y-4">
                  {GETS.map((g, i) => (
                    <li key={i} data-testid={`diag-get-${i + 1}`} className="flex items-start gap-4 border-b border-[#0B2215]/10 pb-4">
                      <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center border border-[#C8A97E] text-[#B39265]">
                        <Check className="h-3 w-3" />
                      </span>
                      <p className="text-base font-light text-[#0B2215] md:text-lg">{g}</p>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="flex flex-col justify-start gap-6 lg:col-span-5 lg:col-start-8">
              <Reveal delay={0.2}>
                <div className="border border-[#C8A97E]/50 bg-[#F0EBE1] p-8 md:p-10">
                  <p className="okaia-overline text-[#B39265]">En pratique</p>
                  <ul className="mt-6 space-y-3 text-base font-light text-[#0B2215]">
                    <li><span className="font-serif-display italic text-[#B39265]">Format</span> — entretien individuel en visioconférence</li>
                    <li><span className="font-serif-display italic text-[#B39265]">Durée</span> — 30 minutes</li>
                    <li><span className="font-serif-display italic text-[#B39265]">Tarif</span> — 49 €</li>
                    <li><span className="font-serif-display italic text-[#B39265]">Préparation</span> — quatre questions courtes lors de la réservation (trois obligatoires, une facultative)</li>
                  </ul>
                  <div className="mt-8">
                    <CTAButton label="Réserver mon diagnostic — 49 €" testId="clarifier-cta-2" href={TIDYCAL_URL} />
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.3}>
                <img src="/illus-visio.jpg" alt="Illustration : échange en visioconférence" className="w-full border border-[#C8A97E]/30" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
